import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { locations } from '../mockData';
import { toast } from 'sonner';

const BookNow = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedGround, setSelectedGround] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [slotServices, setSlotServices] = useState({});
  const [expandedSlots, setExpandedSlots] = useState({});
  const [racketQuantities, setRacketQuantities] = useState({});
  const [playerCounts, setPlayerCounts] = useState({}); // For open/club play

  // Additional services based on location
  const additionalServices = selectedLocation?.id === 1 
    ? [
        { id: 'referee', name: 'Referee', price: 350, hasQuantity: false },
        { id: 'bibs', name: 'Bibs', price: 50, hasQuantity: false }
      ]
    : [
        { id: 'rackets', name: 'Racket Rental', price: 150, hasQuantity: true, unit: 'per racket' }
      ];

  const getGroundsForLocation = () => {
    if (!selectedLocation) return [];
    
    if (selectedLocation.id === 1) {
      // Football Arena - only one 7v7 ground
      return [
        { id: '7v7', name: '7 V 7', type: 'Full Ground', size: '40 BY 30 M', morning: 2000, evening: 2500 }
      ];
    } else {
      // Racquet Sports Arena - 4 pickleball courts
      return [
        { id: 'court-1', name: 'Court 1', type: 'Pickleball Court', size: '20 BY 44 FT', morning: 600, evening: 800 },
        { id: 'court-2', name: 'Court 2', type: 'Pickleball Court', size: '20 BY 44 FT', morning: 600, evening: 800 },
        { id: 'court-3', name: 'Court 3', type: 'Pickleball Court', size: '20 BY 44 FT', morning: 600, evening: 800 },
        { id: 'court-4', name: 'Court 4', type: 'Pickleball Court', size: '20 BY 44 FT', morning: 600, evening: 800 }
      ];
    }
  };

  const allSlots = [
    { id: '05-06', time: '05:00 to 06:00', status: 'available', period: 'morning', type: 'full_court' },
    { id: '06-07', time: '06:00 to 07:00', status: 'available', period: 'morning', type: 'full_court' },
    { id: '07-08', time: '07:00 to 08:00', status: 'available', period: 'morning', type: 'open_play', availableSpots: 8, maxSpots: 8 },
    { id: '08-09', time: '08:00 to 09:00', status: 'available', period: 'morning', type: 'open_play', availableSpots: 5, maxSpots: 8 },
    { id: '09-10', time: '09:00 to 10:00', status: 'available', period: 'morning', type: 'full_court' },
    { id: '10-11', time: '10:00 to 11:00', status: 'available', period: 'morning', type: 'full_court' },
    { id: '11-12', time: '11:00 to 12:00', status: 'available', period: 'morning', type: 'full_court' },
    { id: '12-13', time: '12:00 to 13:00', status: 'available', period: 'afternoon', type: 'full_court' },
    { id: '13-14', time: '13:00 to 14:00', status: 'available', period: 'afternoon', type: 'full_court' },
    { id: '14-15', time: '14:00 to 15:00', status: 'available', period: 'evening', type: 'full_court' },
    { id: '15-16', time: '15:00 to 16:00', status: 'available', period: 'evening', type: 'full_court' },
    { id: '16-17', time: '16:00 to 17:00', status: 'available', period: 'evening', type: 'full_court' },
    { id: '17-18', time: '17:00 to 18:00', status: 'available', period: 'evening', type: 'club_play', availableSpots: 8, maxSpots: 8 },
    { id: '18-19', time: '18:00 to 19:00', status: 'available', period: 'evening', type: 'club_play', availableSpots: 3, maxSpots: 8 },
    { id: '19-20', time: '19:00 to 20:00', status: 'available', period: 'evening', type: 'full_court' },
    { id: '20-21', time: '20:00 to 21:00', status: 'available', period: 'evening', type: 'full_court' },
    { id: '21-22', time: '21:00 to 22:00', status: 'available', period: 'evening', type: 'full_court' },
    { id: '22-23', time: '22:00 to 23:00', status: 'available', period: 'night', type: 'full_court' }
  ];

  const selectLocation = (location) => {
    setSelectedLocation(location);
    setSelectedGround(null);
    setSelectedSlots([]);
  };

  const toggleSlot = (slotId) => {
    const slot = allSlots.find(s => s.id === slotId);
    
    if (selectedSlots.includes(slotId)) {
      setSelectedSlots(selectedSlots.filter(s => s !== slotId));
      // Remove services for this slot
      const newServices = { ...slotServices };
      delete newServices[slotId];
      setSlotServices(newServices);
      // Remove player count
      const newPlayerCounts = { ...playerCounts };
      delete newPlayerCounts[slotId];
      setPlayerCounts(newPlayerCounts);
    } else {
      // For pickleball open/club play, ask for player count
      if (selectedLocation?.id === 2 && (slot.type === 'open_play' || slot.type === 'club_play')) {
        const maxPlayers = Math.min(slot.availableSpots || 8, 8);
        const count = prompt(`How many players? (1-${maxPlayers})`);
        const playerCount = parseInt(count);
        
        if (playerCount && playerCount > 0 && playerCount <= maxPlayers) {
          setSelectedSlots([...selectedSlots, slotId]);
          setSlotServices({ ...slotServices, [slotId]: [] });
          setPlayerCounts({ ...playerCounts, [slotId]: playerCount });
        } else if (count !== null) {
          alert(`Please enter a valid number between 1 and ${maxPlayers}`);
        }
      } else {
        setSelectedSlots([...selectedSlots, slotId]);
        setSlotServices({ ...slotServices, [slotId]: [] });
      }
    }
  };

  const toggleService = (slotId, serviceId) => {
    const currentServices = slotServices[slotId] || [];
    const newServices = currentServices.includes(serviceId)
      ? currentServices.filter(s => s !== serviceId)
      : [...currentServices, serviceId];
    setSlotServices({ ...slotServices, [slotId]: newServices });
    
    // Initialize quantity to 1 if adding a service with quantity
    const service = additionalServices.find(s => s.id === serviceId);
    if (service?.hasQuantity && !currentServices.includes(serviceId)) {
      setRacketQuantities({ ...racketQuantities, [slotId]: 1 });
    }
  };

  const updateRacketQuantity = (slotId, quantity) => {
    const qty = Math.max(1, Math.min(10, parseInt(quantity) || 1));
    setRacketQuantities({ ...racketQuantities, [slotId]: qty });
  };

  const toggleSlotExpansion = (slotId) => {
    setExpandedSlots({ ...expandedSlots, [slotId]: !expandedSlots[slotId] });
  };

  const removeSlot = (slotId) => {
    setSelectedSlots(selectedSlots.filter(s => s !== slotId));
    const newServices = { ...slotServices };
    delete newServices[slotId];
    setSlotServices(newServices);
    const newExpanded = { ...expandedSlots };
    delete newExpanded[slotId];
    setExpandedSlots(newExpanded);
  };

  const calculateSlotTotal = (slotId) => {
    const slot = allSlots.find(s => s.id === slotId);
    const basePrice = getSlotPrice(slot);
    const servicesPrice = (slotServices[slotId] || []).reduce((total, serviceId) => {
      const service = additionalServices.find(s => s.id === serviceId);
      if (!service) return total;
      
      if (service.hasQuantity) {
        const quantity = racketQuantities[slotId] || 1;
        return total + (service.price * quantity);
      }
      return total + service.price;
    }, 0);
    return basePrice + servicesPrice;
  };

  const calculateGrandTotal = () => {
    return selectedSlots.reduce((total, slotId) => total + calculateSlotTotal(slotId), 0);
  };

  const getSlotPrice = (slot) => {
    if (!selectedGround) return 0;
    
    // For pickleball, check slot type
    if (selectedLocation?.id === 2) {
      if (slot.type === 'open_play') {
        const playerCount = playerCounts[slot.id] || 1;
        return 100 * playerCount; // ₹100 per player
      } else if (slot.type === 'club_play') {
        return 0; // Free for club members
      }
    }
    
    // Regular full court booking
    const grounds = getGroundsForLocation();
    const ground = grounds.find(g => g.id === selectedGround);
    if (!ground) return 0;
    
    if (slot.period === 'morning') {
      return ground.morning;
    } else {
      return ground.evening;
    }
  };

  const handleBooking = () => {
    if (!selectedLocation || !selectedDate || !selectedGround || selectedSlots.length === 0) {
      toast.error('Please select location, date, ground and time slots');
      return;
    }
    toast.success('Booking confirmed! You will receive a confirmation shortly.');
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-black py-16 border-b border-yellow-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-6">
            <div className="w-12 h-0.5 bg-yellow-400"></div>
            <div className="w-12 h-12 rounded-full border-2 border-yellow-400 flex items-center justify-center">
              <span className="text-3xl">🎫</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white">BOOKING</h1>
            <div className="w-12 h-12 rounded-full border-2 border-yellow-400 flex items-center justify-center">
              <span className="text-3xl">🎫</span>
            </div>
            <div className="w-12 h-0.5 bg-yellow-400"></div>
          </div>
          <p className="text-center text-gray-300 mt-6 text-lg">
            Checkout our availability and book the date and time that works for you
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Location Selector */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-yellow-400 mb-4">Select Location</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {locations.map((location) => (
              <Card
                key={location.id}
                onClick={() => selectLocation(location)}
                className={`cursor-pointer transition-all ${
                  selectedLocation?.id === location.id
                    ? 'bg-yellow-400/10 border-yellow-400'
                    : 'bg-gray-900 border-yellow-500/20 hover:border-yellow-400'
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-bold text-lg">{location.name}</h3>
                      <p className="text-yellow-400">{location.sport}</p>
                    </div>
                    <div className="text-3xl">
                      {location.sportIcon.startsWith('/') ? (
                        <img src={location.sportIcon} alt={location.sport} className="w-10 h-10" />
                      ) : (
                        location.sportIcon
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {selectedLocation && (
          <>
          <Card className="bg-gray-900 border-yellow-500/20 mb-8">
            <CardContent className="p-8">
              {/* Select Date */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-yellow-400 mb-4">Select Date</h2>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full md:w-auto bg-gray-800 border-2 border-yellow-500/20 text-white px-6 py-3 rounded-lg text-lg focus:outline-none focus:border-yellow-400 hover:border-yellow-400 transition-all"
                />
              </div>

              {/* Select Ground */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-yellow-400 mb-4">
                  Select {selectedLocation.id === 1 ? 'Ground' : 'Court'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {getGroundsForLocation().map((ground) => (
                    <div
                      key={ground.id}
                      onClick={() => setSelectedGround(ground.id)}
                      className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedGround === ground.id
                          ? 'border-yellow-400 bg-yellow-400/10'
                          : 'border-yellow-500/20 bg-gray-800 hover:border-yellow-400'
                      }`}
                    >
                      <h3 className="text-white font-bold text-xl mb-2">{ground.name}</h3>
                      <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${
                        selectedLocation.id === 1 ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
                      }`}>
                        {ground.type}
                      </div>
                      <p className="text-gray-400 text-sm mb-4">Size: {ground.size}</p>
                      <div className="flex justify-between text-sm">
                        <div>
                          <p className="text-gray-500">Morning</p>
                          <p className="text-yellow-400 font-bold">₹{ground.morning}</p>
                          <p className="text-gray-600 text-xs">per hour</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Evening</p>
                          <p className="text-yellow-400 font-bold">₹{ground.evening}</p>
                          <p className="text-gray-600 text-xs">per hour</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Select Slots */}
              <div>
                <h2 className="text-2xl font-bold text-yellow-400 mb-4">Select Slots</h2>
                
                <p className="text-gray-400 mb-4">5:00 AM - 11:00 PM</p>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                  {allSlots.map((slot) => {
                    const price = getSlotPrice(slot);
                    const isPickleball = selectedLocation?.id === 2;
                    const isOpenOrClubPlay = isPickleball && (slot.type === 'open_play' || slot.type === 'club_play');
                    
                    return (
                      <button
                        key={slot.id}
                        onClick={() => slot.status === 'available' && selectedGround && toggleSlot(slot.id)}
                        disabled={slot.status === 'locked' || !selectedGround || (isOpenOrClubPlay && slot.availableSpots === 0)}
                        className={`p-4 rounded-lg border-2 text-center transition-all ${
                          slot.status === 'locked' || !selectedGround || (isOpenOrClubPlay && slot.availableSpots === 0)
                            ? 'bg-red-900/20 border-red-900 text-red-600 cursor-not-allowed opacity-50'
                            : selectedSlots.includes(slot.id)
                            ? 'bg-yellow-400/20 border-yellow-400 text-yellow-400'
                            : 'bg-gray-800 border-gray-700 text-white hover:border-yellow-400'
                        }`}
                      >
                        <div className="font-semibold">{slot.time.split(' to ')[0]}</div>
                        <div className="text-xs text-gray-500">to</div>
                        <div className="font-semibold">{slot.time.split(' to ')[1]}</div>
                        
                        {/* Show slot type and availability for pickleball */}
                        {isPickleball && slot.type === 'open_play' && (
                          <div className="mt-2">
                            <div className="text-xs font-semibold text-green-400">Open Play</div>
                            <div className="text-xs text-gray-400">{slot.availableSpots}/{slot.maxSpots} spots</div>
                          </div>
                        )}
                        {isPickleball && slot.type === 'club_play' && (
                          <div className="mt-2">
                            <div className="text-xs font-semibold text-blue-400">Club Play</div>
                            <div className="text-xs text-gray-400">{slot.availableSpots}/{slot.maxSpots} spots</div>
                          </div>
                        )}
                        
                        {selectedGround && (
                          <div className="text-yellow-400 font-bold text-sm mt-2">
                            {isPickleball && slot.type === 'open_play' && '₹100/player'}
                            {isPickleball && slot.type === 'club_play' && 'Free (Members)'}
                            {(!isPickleball || slot.type === 'full_court') && `₹${price}`}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-yellow-400 rounded mr-2"></div>
                    <span className="text-gray-400">Selected</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-gray-600 rounded mr-2"></div>
                    <span className="text-gray-400">Available</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-red-900 rounded mr-2"></div>
                    <span className="text-gray-400">Locked</span>
                  </div>
                  {selectedLocation.id === 1 && (
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-blue-600 rounded mr-2"></div>
                      <span className="text-gray-400">Full Ground</span>
                    </div>
                  )}
                  {selectedLocation.id === 2 && (
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-green-600 rounded mr-2"></div>
                      <span className="text-gray-400">Pickleball Court</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selected Slots Summary */}
          {selectedSlots.length > 0 && (
            <Card className="bg-gray-900 border-yellow-500/20 mb-8">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-yellow-400 mb-6">
                  Selected Slots ({selectedSlots.length})
                </h2>

                <div className="mb-4">
                  <p className="text-white text-lg font-semibold">
                    {new Date(selectedDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className={`w-3 h-3 rounded-full ${selectedLocation.id === 1 ? 'bg-blue-600' : 'bg-green-600'}`}></div>
                    <p className="text-gray-400">
                      {getGroundsForLocation().find(g => g.id === selectedGround)?.name}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {selectedSlots.map((slotId) => {
                    const slot = allSlots.find(s => s.id === slotId);
                    const isExpanded = expandedSlots[slotId];
                    const slotPrice = getSlotPrice(slot);
                    const isPickleball = selectedLocation?.id === 2;
                    const playerCount = playerCounts[slotId];
                    
                    // Get slot type label
                    let slotTypeLabel = '';
                    if (isPickleball && slot.type === 'open_play') {
                      slotTypeLabel = 'Open Play';
                    } else if (isPickleball && slot.type === 'club_play') {
                      slotTypeLabel = 'Club Play';
                    } else {
                      slotTypeLabel = getGroundsForLocation().find(g => g.id === selectedGround)?.name;
                    }
                    
                    return (
                      <div key={slotId} className="bg-gray-800 border-l-4 border-yellow-400 rounded-lg overflow-hidden">
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-white text-lg font-semibold">{slot.time}</p>
                              <p className="text-gray-400 text-sm">
                                {slotTypeLabel}
                                {playerCount && ` - ${playerCount} player${playerCount > 1 ? 's' : ''}`}
                                {' - '}₹{slotPrice}
                              </p>
                              
                              {/* Show added services when collapsed */}
                              {!isExpanded && (slotServices[slotId] || []).length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {(slotServices[slotId] || []).map((serviceId) => {
                                    const service = additionalServices.find(s => s.id === serviceId);
                                    if (!service) return null;
                                    
                                    const quantity = service.hasQuantity ? (racketQuantities[slotId] || 1) : 1;
                                    const serviceTotal = service.price * quantity;
                                    
                                    return (
                                      <p key={serviceId} className="text-yellow-400 text-sm font-medium">
                                        + {service.hasQuantity ? `${quantity} Rackets` : service.name} - ₹{serviceTotal}
                                      </p>
                                    );
                                  })}
                                  <p className="text-white text-sm font-semibold mt-1">
                                    Slot Total: ₹{calculateSlotTotal(slotId)}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleSlotExpansion(slotId)}
                                className="p-2 text-gray-400 hover:text-white transition-colors"
                              >
                                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                              </button>
                              <button
                                onClick={() => removeSlot(slotId)}
                                className="p-2 text-red-400 hover:text-red-500 transition-colors"
                              >
                                <Trash2 size={20} />
                              </button>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="mt-4 space-y-2">
                              <p className="text-yellow-400 text-sm font-semibold mb-3">Additional Services:</p>
                              {additionalServices.map((service) => {
                                const isChecked = (slotServices[slotId] || []).includes(service.id);
                                const quantity = racketQuantities[slotId] || 1;
                                
                                return (
                                  <div key={service.id}>
                                    <label
                                      className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleService(slotId, service.id)}
                                        className="w-5 h-5 rounded border-gray-600 text-yellow-400 focus:ring-yellow-400 focus:ring-offset-gray-800"
                                      />
                                      <span className="text-white flex-1">{service.name}</span>
                                      <span className="text-yellow-400 font-semibold">
                                        ₹{service.price}
                                        {service.hasQuantity && ` ${service.unit}`}
                                      </span>
                                    </label>
                                    
                                    {service.hasQuantity && isChecked && (
                                      <div className="ml-8 mt-2 flex items-center gap-3 p-3 bg-gray-700/30 rounded-lg">
                                        <label className="text-gray-300 text-sm">Quantity:</label>
                                        <input
                                          type="number"
                                          min="1"
                                          max="10"
                                          value={quantity}
                                          onChange={(e) => updateRacketQuantity(slotId, e.target.value)}
                                          className="w-20 bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded-lg focus:outline-none focus:border-yellow-400"
                                        />
                                        <span className="text-gray-400 text-sm">
                                          Total: <span className="text-yellow-400 font-semibold">₹{service.price * quantity}</span>
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {!isExpanded && (
                            <button
                              onClick={() => toggleSlotExpansion(slotId)}
                              className="mt-3 text-blue-400 text-sm font-semibold hover:text-blue-300 transition-colors"
                            >
                              Add Services
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Total */}
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <div className="flex justify-between items-center">
                    <span className="text-white text-xl font-bold">Total Amount:</span>
                    <span className="text-yellow-400 text-2xl font-bold">₹{calculateGrandTotal()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          </>
        )}

        {/* Book Now Button */}
        {selectedSlots.length > 0 && (
          <div className="text-center">
            <Button
              onClick={handleBooking}
              size="lg"
              className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg px-12 py-6"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookNow;
